import { FC, useEffect, useState } from 'react';
import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import {
	GetSimulationListParam,
	useSimulationList,
} from '@/libs/fleet-api/services/mpp';
import { Button, Checkbox, Col, Form, Input, Row, Table } from 'antd';

export const useSimulationModal = () => {
	return useAsyncModal<
		{
			disabledIds: number[];
			spfId: number;
		},
		{ simulationId: number; simulationNo: string } | undefined
	>();
};

export type SimulationModalProps = ReturnType<typeof useSimulationModal>;

const DEFAULT_PARAM: GetSimulationListParam = {
	pagination: {
		pageNumber: 1,
		pageSize: 10,
	},
	dealerName: '',
};

const SimulationModal: FC<SimulationModalProps> = ({ modal, data, close }) => {
	const [param, setParam] = useState<GetSimulationListParam>(DEFAULT_PARAM);
	const [checkedId, setCheckedId] = useState<number>();
	const [selectedSimulation, setSelectedSimulation] = useState<{
		simulationId: number;
		simulationNo: string;
	}>();

	useEffect(() => {
		if (data?.spfId) setParam({ ...DEFAULT_PARAM, spfId: data.spfId });
	}, [data?.spfId]);

	const simulationQuery = useSimulationList(param);

	const handleTableChange = (page: number, pageSize: number) => {
		setParam((prev) => ({
			...prev,
			pagination: {
				pageNumber: page,
				pageSize,
			},
		}));
	};

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="BACK"
			okText="NEXT"
			okButtonProps={{
				disabled: !selectedSimulation,
			}}
			onOk={() => close(selectedSimulation)}
			onCancel={() => close()}
			afterOpenChange={() => {
				setCheckedId(undefined);
				setSelectedSimulation(undefined);
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Pilih Simulasi</FleetTitle>
			</div>
			<div style={{ marginBottom: 20 }}>
				<Form
					onFinish={(values: { keyword: string }) => {
						setParam((current) => {
							return {
								...current,
								dealerName: values.keyword,
							};
						});
					}}
				>
					<Row gutter={40}>
						<Col flex="auto">
							<Form.Item
								name="keyword"
								label="Dealer"
								labelAlign="left"
								colon={false}
								labelCol={{ style: { width: 150 } }}
							>
								<Input placeholder="Cari nama dealer" />
							</Form.Item>
						</Col>
						<Col flex="none">
							<Button
								htmlType="submit"
								type="primary"
								disabled={simulationQuery.isFetching}
								loading={simulationQuery.isFetching}
								style={{ width: 200 }}
							>
								SEARCH
							</Button>
						</Col>
					</Row>
				</Form>
			</div>
			<Table
				bordered
				dataSource={
					simulationQuery.data?.data?.items?.map((item, key) => {
						return {
							...item,
							key,
						};
					}) ?? []
				}
				rowKey="simulationId"
				loading={simulationQuery.isFetching}
				pagination={{
					current: param.pagination.pageNumber,
					pageSize: param.pagination.pageSize,
					total: simulationQuery.data?.data.totalCount || 0,
					onChange: handleTableChange,
				}}
				scroll={{ x: 'max-content' }}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						render: (_, __, index) => index + 1,
					},
					{
						title: 'No.Simulasi',
						render: (_, record) => record.simulationNo ?? '-',
					},
					{
						title: 'Merk/Type',
						render: (_, record) => record.brandName ?? '-',
					},
					{
						title: 'Dealer',
						render: (_, record) => record.dealerName ?? '-',
					},
					{
						title: 'Pilih Simulasi',
						align: 'center',
						onHeaderCell: () => ({
							style: {
								width: 1,
								whiteSpace: 'nowrap',
							},
						}),
						render: (_, record) => (
							<Checkbox
								disabled={data?.disabledIds.includes(record.simulationId)}
								checked={record.simulationId === checkedId}
								onChange={(e) => {
									if (!e.target.checked) return;
									setCheckedId(record.simulationId);
									setSelectedSimulation({
										simulationId: record.simulationId,
										simulationNo: record.simulationNo,
									});
								}}
							/>
						),
					},
				]}
			/>
		</Modal>
	);
};

export default SimulationModal;
